import { useState } from 'react';
import { AlertTriangle, ArrowLeft, ArrowRight, Eye, Lightbulb, Megaphone, MousePointerClick, Search } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

import { AdsPerformanceChart } from '@/components/ads/AdsPerformanceChart';
import { BudgetCard } from '@/components/ads/BudgetCard';
import { ShopMap } from '@/components/ads/ShopMap';
import { ColumnTable } from '@/components/ui/column-table';
import { EmptyState } from '@/components/ui/empty-state';
import { KpiTile } from '@/components/ui/kpi-tile';
import { SectionHeader } from '@/components/ui/section-header';
import { Skeleton } from '@/components/ui/skeleton';
import { SoftCard } from '@/components/ui/soft-card';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { useLocalAds, useLocalAdsAverages } from '@/hooks/useInsights';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import myShopClient from '@services/nearlive';
import { LIA_INSIGHT_METRICS, SUMMARY_ROWS } from '@/lib/liaInsights';
import { PageScore } from '@/components/overview/LocalPotentialHeader';

const numberFormatter = new Intl.NumberFormat('en-GB');

function formatNumber(n) {
	return numberFormatter.format(Number(n) || 0);
}

function AdsLoadingState() {
	return (
		<div className="space-y-10">
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Ads</h1>
					<p className="mt-2 max-w-2xl text-muted-foreground">Loading your Local Inventory Ads performance…</p>
				</div>
			</header>
			<SoftCard className="h-44 animate-pulse" />
			<section className="grid grid-cols-2 gap-4 md:grid-cols-4">
				{[0, 1, 2, 3].map((i) => (
					<SoftCard key={i} className="min-h-[170px] !p-5">
						<Skeleton className="mx-auto h-3.5 w-2/3" />
						<Skeleton className="mx-auto mt-4 h-8 w-1/2" />
						<Skeleton className="mx-auto mt-4 h-5 w-1/3" />
					</SoftCard>
				))}
			</section>
			<SoftCard className="h-72 animate-pulse" />
		</div>
	);
}

/**
 * Newly-activated shops haven't accumulated any Google Ads insights yet
 * (Google takes a few hours to start serving Local Inventory Ads after a
 * plan goes live). Rather than rendering the full populated dashboard with
 * zeroes everywhere — which reads as "broken" — show a warming-up state
 * that keeps the BudgetCard available (so the user can still tune their
 * commitment) and previews the metrics they'll see once data lands.
 */
function AdsWarmingUpState({ budget, onSave }) {
	return (
		<div className="space-y-10">
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Ads</h1>
					<p className="mt-2 max-w-2xl text-muted-foreground">
						Your ads are live. Google takes a few hours to start showing them — your real numbers will land here as soon as the
						first impressions come in.
					</p>
				</div>
			</header>

			<BudgetCard initialDaily={budget.daily ?? 5} onSave={onSave} />

			<EmptyState
				icon={Megaphone}
				eyebrow="Coming in the next 24 hours"
				title="Your first results are warming up"
				body="It usually takes Google a few hours to start serving Local Inventory Ads. Your first impressions, clicks and search terms will appear here as soon as they start coming through."
				previewTitle="What you'll see here"
				preview={[
					{ icon: Eye, label: 'Local shoppers reached', detail: 'Impressions across Google and Maps' },
					{ icon: MousePointerClick, label: 'Clicks to your store', detail: 'Calls, directions, product views' },
					{ icon: Search, label: 'Search terms locals are using', detail: 'See exactly how they find you' }
				]}
			/>
		</div>
	);
}

function AdsEmptyState({ onActivate, startOnBudget = false, hasCard = false, onAddCard, addingCard = false, cardError = null }) {
	const { shopPath } = useShopPath();
	// Don't open the budget step without a card on file — there's nothing to
	// activate yet, so the no-card path routes to "add a card" first.
	const [showingBudget, setShowingBudget] = useState(startOnBudget && hasCard);

	if (showingBudget) {
		return (
			<div className="space-y-8">
				<button
					type="button"
					onClick={() => setShowingBudget(false)}
					className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
				>
					<ArrowLeft className="h-3.5 w-3.5" /> Back
				</button>
				<header>
					<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Activate Local Ads</h1>
					<p className="mt-2 max-w-2xl text-muted-foreground">
						Choose your daily budget. You can stop or change it any time — we&rsquo;ll never spend more than this per day.
					</p>
				</header>
				<BudgetCard mode="activation" initialDaily={5} onSave={onActivate} />
			</div>
		);
	}

	// Local Ads bills daily as the retailer spends (NEA-5753), so a card has to
	// be on file before activation. With no card the primary CTA collects one
	// via Stripe Checkout instead of opening the budget step; the backend
	// (REQUIRE_PAYMENT_METHOD_FOR_LIA) enforces the same rule as a backstop.
	const noCard = !hasCard;

	return (
		<div className="space-y-10">
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Ads</h1>
					<p className="mt-2 text-muted-foreground">Put your in-store stock in front of locals searching on Google right now.</p>
				</div>
				<div className="ml-auto shrink-0">
					<PageScore />
				</div>
			</header>
			<EmptyState
				icon={Megaphone}
				eyebrow="Local Ads — your secret weapon"
				title="Reach shoppers searching nearby today"
				body={
					noCard
						? "Local Ads bills daily as you spend, so you'll need a card on file before you can switch it on. Add one and you're ready to go."
						: "Local Ads automatically promote what's in stock to people Googling nearby. Most retailers see over £5 of in-store sales for every £1 spent."
				}
				primaryCta={noCard ? (addingCard ? 'Redirecting…' : 'Add a payment method') : 'Start with £5/day'}
				onPrimaryClick={noCard ? onAddCard : onActivate ? () => setShowingBudget(true) : undefined}
				primaryHref={shopPath('/channels/local-ads')}
				secondaryCta="See how it works"
				secondaryHref={shopPath('/channels/local-ads')}
				previewTitle="What you'll start tracking"
				preview={[
					{ icon: Eye, label: 'Impressions in your local area', detail: 'How many people saw your products' },
					{ icon: MousePointerClick, label: 'Clicks straight to your store', detail: 'Calls, directions and product views' },
					{ icon: Search, label: 'Search terms locals are using', detail: 'Spot trends before competitors do' }
				]}
			/>
			{noCard && cardError ? <p className="mt-4 text-sm font-medium text-destructive">{cardError}</p> : null}
		</div>
	);
}

export function Ads() {
	const { shop, isLoading: shopLoading } = useShop();
	const { data, isLoading: adsLoading, updateBudget, activate } = useLocalAds(shop?.id);
	const { billing, isLoading: billingLoading } = useBillingStatus(shop?.id);
	const { data: averages } = useLocalAdsAverages();
	const { shopPath } = useShopPath();
	const [searchParams] = useSearchParams();
	const [addingCard, setAddingCard] = useState(false);
	const [cardError, setCardError] = useState(null);
	const loading = shopLoading || adsLoading || billingLoading;

	// A card on file is required before Local Ads can be switched on (it bills
	// daily as you spend). `hasPaymentMethod` comes from the same billing read
	// AppLayout already loaded, so this is a warm SWR cache hit, not a new call.
	const hasCard = Boolean(billing?.hasPaymentMethod);

	// No card → send the retailer to Stripe Checkout (mode: setup) to add one,
	// mirroring the Billing page's "Add a card" CTA. It returns to the Billing
	// page; they come back to Ads and activate (now card-on-file). Card-checkout
	// 403s under HQ impersonation by design — surface that rather than swallow.
	const addCard = async () => {
		if (!shop?.id) return;
		setAddingCard(true);
		setCardError(null);
		try {
			const client = await myShopClient();
			const { data: session } = await client.post(`/api/billing/card-checkout/${shop.id}`);
			if (!session?.url) throw new Error('Stripe Checkout returned no redirect URL');
			window.location.assign(session.url);
		} catch (err) {
			setCardError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Could not start card setup');
			setAddingCard(false);
		}
	};

	// `/api/local-ads/average-results` aggregates `shop.weeklyInsights` across
	// shops with a live plan. In production it produces a real benchmark; for
	// new tenants and the dev environment it returns 0/0. We fall back to the
	// industry-published Local Inventory Ads benchmark (~1.8% CTR), which is
	// what the Lovable design uses verbatim. Keeps the caption meaningful
	// rather than empty.
	const INDUSTRY_FALLBACK_CTR = '1.80';
	const benchmarkCtr =
		averages && Number(averages.impressions) > 0
			? ((Number(averages.clicks) / Number(averages.impressions)) * 100).toFixed(2)
			: INDUSTRY_FALLBACK_CTR;
	const ctrIndustryCaption = `Industry average: ${benchmarkCtr}%`;

	if (loading) return <AdsLoadingState />;
	if (!data?.enabled)
		return (
			<AdsEmptyState
				onActivate={shop?.id ? activate : undefined}
				startOnBudget={searchParams.get('activate') === '1'}
				hasCard={hasCard}
				onAddCard={addCard}
				addingCard={addingCard}
				cardError={cardError}
			/>
		);

	const summary = data.summary || {};
	const budget = data.budget || { daily: 0, monthly: 0 };
	const series = Array.isArray(data.daily) ? data.daily : [];
	const topProducts = Array.isArray(data.topProducts) ? data.topProducts : [];
	const searchTerms = Array.isArray(data.searchTerms) ? data.searchTerms : [];
	const locations = Array.isArray(data.locations) ? data.locations : [];
	const clicksToSales = Array.isArray(data.clicksToSales) ? data.clicksToSales : [];

	// Just-activated shops have an enabled plan but zero Google-Ads-side data
	// yet. Render a warming-up surface instead of a populated dashboard full
	// of zeros — keep the BudgetCard so they can tune their commitment.
	if (!summary.impressions && !summary.clicks) return <AdsWarmingUpState budget={budget} onSave={updateBudget} />;

	const cpcDisplay = summary.cpc ? `£${Number(summary.cpc).toFixed(2)}` : '£0.00';
	const ctrDisplay = `${Number(summary.ctr || 0).toFixed(2)}%`;
	const monthlySpendDisplay = `£${formatNumber(summary.monthlySpend || budget.monthly || 0)}`;

	return (
		<div className="space-y-10">
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Ads</h1>
					<p className="mt-2 max-w-2xl text-muted-foreground">
						Activate or change your ads budget to appear at the top of Google results and promote your products to more local
						shoppers.
					</p>
				</div>
				<div className="ml-auto shrink-0">
					<PageScore />
				</div>
			</header>

			{data.pausedForBilling ? (
				<SoftCard className="flex flex-wrap items-start gap-4 border border-destructive/30 bg-destructive/5">
					<AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-destructive" />
					<div className="min-w-0 flex-1">
						<h3 className="text-base font-bold text-destructive">Your ads are paused because a payment failed</h3>
						<p className="mt-1 text-sm text-muted-foreground">
							We couldn't charge your card for recent ad spend, so we've paused your Google Ads campaigns. Update your payment
							method to resume them — we'll restart automatically once the outstanding invoice clears.
						</p>
					</div>
					<Link
						to="/settings/billing"
						className="inline-flex items-center gap-1.5 rounded-full bg-destructive px-4 py-2 text-sm font-semibold text-destructive-foreground transition hover:opacity-90"
					>
						Update payment <ArrowRight className="h-4 w-4" />
					</Link>
				</SoftCard>
			) : null}

			<BudgetCard initialDaily={budget.daily ?? 5} onSave={updateBudget} />

			<section>
				<SectionHeader
					eyebrow="Performance"
					title="How your ads are performing"
					sub="Headline numbers and daily trend, compared to the previous period."
				/>
				<div className="grid grid-cols-2 gap-4 md:grid-cols-4">
					<KpiTile label="Local shoppers reached" value={formatNumber(summary.impressions)} delta={summary.impressionsChange} />
					<KpiTile label="Clicks to your store" value={formatNumber(summary.clicks)} delta={summary.clicksChange} />
					<KpiTile label="Click-through rate" value={ctrDisplay} sub={ctrIndustryCaption} />
					<KpiTile label="Cost per click" value={cpcDisplay} sub={`${monthlySpendDisplay} ad spend this month`} />
				</div>
				<SoftCard className="mt-4 p-5 md:p-6">
					<AdsPerformanceChart series={series} />
				</SoftCard>
			</section>

			<section className="grid grid-cols-1 gap-5 xl:grid-cols-2">
				<div>
					<SectionHeader title={LIA_INSIGHT_METRICS.products.title} sub={LIA_INSIGHT_METRICS.products.sub} />
					<ColumnTable
						rows={topProducts.slice(0, SUMMARY_ROWS)}
						emptyLabel={LIA_INSIGHT_METRICS.products.emptyLabel}
						viewMoreHref={shopPath('/ads/insights/products')}
						columns={LIA_INSIGHT_METRICS.products.columns}
					/>
				</div>
				<div>
					<SectionHeader title={LIA_INSIGHT_METRICS['search-terms'].title} sub={LIA_INSIGHT_METRICS['search-terms'].sub} />
					<ColumnTable
						rows={searchTerms.slice(0, SUMMARY_ROWS)}
						emptyLabel={LIA_INSIGHT_METRICS['search-terms'].emptyLabel}
						viewMoreHref={shopPath('/ads/insights/search-terms')}
						columns={LIA_INSIGHT_METRICS['search-terms'].columns}
					/>
				</div>
			</section>

			<section>
				<SectionHeader
					eyebrow="Advanced insights"
					title="Where your ads are appearing"
					sub="The local areas where shoppers are seeing and clicking your ads."
				/>
				<div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
					<SoftCard className="overflow-hidden p-0 xl:col-span-2">
						<div className="grid grid-cols-1 md:grid-cols-2">
							<div className="h-full border-b border-border/60 md:border-b-0 md:border-r">
								<ShopMap shop={shop} label={shop?.city || locations[0]?.location} radiusKm={data.radiusKm} />
							</div>
							<div>
								<div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-6 border-b border-border/60 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
									<div>Location</div>
									<div className="w-16 text-right">Clicks</div>
									<div className="w-20 text-right">Views</div>
								</div>
								<div className="divide-y divide-border/60">
									{locations.length === 0 ? (
										<div className="px-5 py-6 text-center text-sm text-muted-foreground">No geographic data yet.</div>
									) : (
										locations.map((l) => (
											<div
												key={l.location}
												className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-6 px-5 py-3"
											>
												<div className="truncate text-sm font-medium">{l.location}</div>
												<div className="w-16 text-right text-sm font-semibold tabular-nums text-foreground">
													{formatNumber(l.clicks)}
												</div>
												<div className="w-20 text-right text-sm tabular-nums text-muted-foreground">
													{formatNumber(l.views)}
												</div>
											</div>
										))
									)}
								</div>
							</div>
						</div>
					</SoftCard>

					<SoftCard tone="muted">
						<div className="flex items-center gap-2 text-sm font-bold text-foreground">
							<Lightbulb className="h-4 w-4 text-primary" /> How can I use these insights?
						</div>
						<p className="mt-3 text-sm leading-relaxed text-muted-foreground">
							Spotting which areas drive the most clicks helps you decide where to focus stock and promotions. If a nearby
							town is sending lots of views but few clicks, your product titles or photos may need a refresh.
						</p>
						<p className="mt-3 text-sm leading-relaxed text-muted-foreground">
							Want to expand your radius?{' '}
							<button className="font-semibold text-primary hover:underline">Contact support</button>.
						</p>
					</SoftCard>
				</div>
			</section>

			<section>
				<SectionHeader title={LIA_INSIGHT_METRICS['clicks-to-sales'].title} sub={LIA_INSIGHT_METRICS['clicks-to-sales'].sub} />
				<ColumnTable
					rows={clicksToSales.slice(0, SUMMARY_ROWS)}
					emptyLabel={LIA_INSIGHT_METRICS['clicks-to-sales'].emptyLabel}
					viewMoreHref={shopPath('/ads/insights/clicks-to-sales')}
					columns={LIA_INSIGHT_METRICS['clicks-to-sales'].columns}
				/>
			</section>

			<SoftCard tone="soft" className="flex flex-wrap items-center gap-4">
				<div className="min-w-0 flex-1">
					<h3 className="text-lg font-bold">Your ads are paying back</h3>
					<p className="mt-1 max-w-xl text-sm text-muted-foreground">
						For every £1 you spend, you're being seen by more local shoppers. Bumping your budget will help you grow further.
					</p>
				</div>
				<a
					href="#top"
					className="inline-flex items-center gap-1.5 rounded-full bg-action px-5 py-2.5 text-sm font-semibold text-action-foreground transition hover:opacity-90"
					onClick={(e) => {
						e.preventDefault();
						window.scrollTo({ top: 0, behavior: 'smooth' });
					}}
				>
					Adjust budget <ArrowRight className="h-4 w-4" />
				</a>
			</SoftCard>
		</div>
	);
}
